/**
 * FlowHub - Login Page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Alert, message, Modal, Divider } from 'antd';
import { MailOutlined, LockOutlined, SafetyOutlined, GoogleOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import AuthLayout from '../components/AuthLayout';
import { supabase } from '@/shared/lib/supabase';

interface LoginFormValues {
  email: string;
  password: string;
}

function LoginPage() {
  const { signIn, verifyMfa, mfaRequired, clearMfaState } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [verifyingMfa, setVerifyingMfa] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingResetEmail, setSendingResetEmail] = useState(false);

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      setError(null);
      
      await signIn(values.email, values.password);
      
      // Se MFA não for necessário, signIn já faz loadUser e o router redireciona
      if (!useAuthStore.getState().mfaRequired) {
        message.success('Login realizado com sucesso!');
      }
      // Se MFA for necessário, o modal será aberto automaticamente
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Email ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    try {
      setVerifyingMfa(true);
      setError(null);

      if (!mfaCode || mfaCode.length !== 6) {
        setError('Digite um código válido de 6 dígitos');
        return;
      }

      await verifyMfa(mfaCode);
      
      message.success('Login realizado com sucesso!');
      setMfaCode('');
    } catch (err: any) {
      console.error('MFA verification error:', err);
      setError('Código inválido. Tente novamente.');
    } finally {
      setVerifyingMfa(false);
    }
  };

  const handleMfaCancel = () => {
    clearMfaState();
    setMfaCode('');
    setError(null);
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/client/dashboard`,
        },
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Google login error:', err);
      setError(err.message || 'Erro ao fazer login com Google');
    }
  };

  const handleResetPassword = async () => {
    try {
      setSendingResetEmail(true);
      
      if (!resetEmail) {
        message.error('Digite seu email');
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(resetEmail)) {
        message.error('Digite um email válido');
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      message.success('Email de recuperação enviado! Verifique sua caixa de entrada.');
      setResetPasswordModalOpen(false);
      setResetEmail('');
    } catch (err: any) {
      console.error('Reset password error:', err);
      message.error(err.message || 'Erro ao enviar email de recuperação');
    } finally {
      setSendingResetEmail(false);
    }
  };

  return (
    <AuthLayout
      title="Bem-vindo de volta"
      subtitle="Acesse sua conta para ativar automações, gerenciar workflows e acompanhar sua operação."
      features={[
        'Acesso seguro e criptografado',
        'Proteção de dados garantida',
        'Suporte dedicado 24/7'
      ]}
      stats={[
        { label: 'Tempo de resposta', value: '< 2 seg' },
        { label: 'Uptime garantido', value: '99.9%' }
      ]}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          className="mb-6 rounded-xl shadow-lg"
        />
      )}

      <Form<LoginFormValues>
        name="login"
        onFinish={handleSubmit}
        layout="vertical"
        size="large"
        autoComplete="off"
        className="space-y-4"
      >
        <Form.Item
          name="email"
          label={<span className="text-slate-700 font-medium">Email</span>}
          rules={[
            { required: true, message: 'Informe seu email' },
            { type: 'email', message: 'Digite um email válido' },
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-violet-500" />}
            placeholder="nome@empresa.com"
            autoComplete="email"
            className="rounded-xl h-12 border-slate-200 hover:border-violet-400 focus:border-violet-500"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={<span className="text-slate-700 font-medium">Senha</span>}
          rules={[{ required: true, message: 'Informe sua senha' }]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-violet-500" />}
            placeholder="••••••••"
            autoComplete="current-password"
            className="rounded-xl h-12 border-slate-200 hover:border-violet-400 focus:border-violet-500"
          />
        </Form.Item>

        <div className="text-sm text-slate-500">
          Esqueceu a senha?{' '}
          <button
            type="button"
            onClick={() => setResetPasswordModalOpen(true)}
            className="text-violet-600 hover:text-pink-600 font-medium transition-colors"
          >
            Recuperar acesso
          </button>
        </div>

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          size="large"
          className="!h-12 !rounded-xl !bg-gradient-to-r !from-violet-600 !to-pink-600 hover:!from-violet-700 hover:!to-pink-700 !border-0 !shadow-lg !shadow-violet-500/50 !font-semibold !text-base"
        >
          Entrar
        </Button>
      </Form>

      <Divider className="!text-slate-400 !text-sm">ou</Divider>

      <Button
        icon={<GoogleOutlined />}
        onClick={handleGoogleLogin}
        block
        size="large"
        className="!h-12 !rounded-xl !border-slate-200 hover:!border-violet-400 !font-medium"
      >
        Continuar com Google
      </Button>

      <div className="mt-8 text-center text-sm text-slate-600">
        Ainda não possui conta?{' '}
        <Link
          to="/register"
          className="font-semibold text-violet-600 hover:text-pink-600 transition-colors"
        >
          Criar conta
        </Link>
      </div>

      {/* Modal de Verificação 2FA */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <SafetyOutlined className="text-violet-600" />
            <span>Autenticação em Dois Fatores</span>
          </div>
        }
        open={mfaRequired}
        onOk={handleMfaVerify}
        onCancel={handleMfaCancel}
        okText="Verificar"
        cancelText="Cancelar"
        confirmLoading={verifyingMfa}
        className="mfa-modal"
      >
        <div className="py-4 space-y-4">
          <Alert
            message="Código de Autenticação"
            description="Digite o código de 6 dígitos do seu aplicativo autenticador (Google Authenticator, Authy, etc.)"
            type="info"
            showIcon
            icon={<SafetyOutlined />}
          />
          
          {error && (
            <Alert
              message={error}
              type="error"
              closable
              onClose={() => setError(null)}
            />
          )}

          <Input
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            maxLength={6}
            size="large"
            className="text-center text-xl tracking-widest"
            autoFocus
            onPressEnter={handleMfaVerify}
          />
          
          <p className="text-sm text-slate-500 text-center">
            Este código expira em 30 segundos. Se não funcionar, aguarde e tente o próximo código.
          </p>
        </div>
      </Modal>

      {/* Modal de Recuperação de Senha */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <LockOutlined className="text-violet-600" />
            <span>Recuperar Senha</span>
          </div>
        }
        open={resetPasswordModalOpen}
        onOk={handleResetPassword}
        onCancel={() => {
          setResetPasswordModalOpen(false);
          setResetEmail('');
        }}
        okText="Enviar email"
        cancelText="Cancelar"
        confirmLoading={sendingResetEmail}
      >
        <div className="py-4 space-y-4">
          <Alert
            message="Recuperação de Senha"
            description="Digite seu email cadastrado. Enviaremos um link para você redefinir sua senha."
            type="info"
            showIcon
          />

          <Input
            prefix={<MailOutlined className="text-violet-500" />}
            placeholder="seu@email.com"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            size="large"
            type="email"
            onPressEnter={handleResetPassword}
          />
          
          <p className="text-sm text-slate-500">
            Após enviar, verifique sua caixa de entrada e a pasta de spam. O link expira em 1 hora.
          </p>
        </div>
      </Modal>
    </AuthLayout>
  );
}

export default LoginPage;
